import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';
import { useTenant } from './contexts/TenantContext';

function StaffAnalytics({ staffId, staffName, onClose }) {
  const { tenant } = useTenant();
  const isSmokeShop = ['smoke_shop','smoke','vape','dispensary'].includes(tenant?.business_type);
  const [range, setRange] = useState('month');
  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [commissionRate, setCommissionRate] = useState(0);
  const [editRate, setEditRate] = useState(false);
  const [rateInput, setRateInput] = useState('0');
  const [expandedSale, setExpandedSale] = useState(null);
  const [receiptSale, setReceiptSale] = useState(null);
  const tid = tenant?.id;

  useEffect(() => {
    if (isSmokeShop) loadSalesData();
    else loadBookingAnalytics();
  }, [staffId, range, isSmokeShop]);

  async function loadSalesData() {
    setLoading(true);
    try {
      const r = await axios.get('/api/smokeshop-sales?limit=2000');
      const all = r.data.sales || [];
      const now = new Date();
      const filtered = all.filter(s => {
        if (s.staff_name !== staffName) return false;
        const d = new Date(s.created_at);
        if (range === 'today') return d.toDateString() === now.toDateString();
        if (range === 'week') { const w = new Date(now); w.setDate(now.getDate()-7); return d >= w; }
        if (range === 'month') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        return true;
      });
      setSales(filtered);
      const saved = JSON.parse(localStorage.getItem('commission_rates_' + tid) || '{}');
      const rate = saved[staffName] || 0;
      setCommissionRate(rate);
      setRateInput(String(rate));
    } catch(e) {}
    setLoading(false);
  }

  async function loadBookingAnalytics() {
    setLoading(true);
    try {
      const r = await axios.get('/api/staff-analytics/' + staffId + '?range=' + range);
      if (r.data.success) setAnalytics(r.data);
    } catch(e) {}
    setLoading(false);
  }

  function saveRate() {
    const rate = parseFloat(rateInput) || 0;
    const saved = JSON.parse(localStorage.getItem('commission_rates_' + tid) || '{}');
    saved[staffName] = rate;
    localStorage.setItem('commission_rates_' + tid, JSON.stringify(saved));
    setCommissionRate(rate);
    setEditRate(false);
  }

  function formatDateTime(dt) {
    if (!dt) return '—';
    const d = new Date(dt);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' · ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const C = { bg: '#0f0f11', panel: '#16161a', border: '#2a2a35', text: '#f0f0f2', muted: '#7a7a8a', amber: '#f5a623', green: '#3ecf8e', red: '#e8484a', blue: '#4f8ef7' };
  const inp = { padding: '7px 10px', background: '#1a1a22', border: '1px solid ' + C.border, borderRadius: 8, color: C.text, fontSize: 13, outline: 'none' };
  const payColors = { cash: '#3ecf8e', card: '#4f8ef7', venmo: '#a78bfa', zelle: '#f5a623' };

  const totalSales = sales.reduce((s, t) => s + parseFloat(t.total || 0), 0);
  const avgSale = sales.length > 0 ? totalSales / sales.length : 0;
  const commission = totalSales * (commissionRate / 100);
  const payBreakdown = sales.reduce((acc, s) => { acc[s.payment_method] = (acc[s.payment_method] || 0) + 1; return acc; }, {});

  if (isSmokeShop) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: C.bg, border: '1px solid ' + C.border, borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid ' + C.border, display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: C.bg, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.text }}>💰 {staffName}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>Sales Performance</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 24, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ padding: '20px 24px' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            {['today','week','month','all'].map(r => (
              <button key={r} onClick={() => setRange(r)} style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, background: range === r ? C.amber : C.panel, color: range === r ? '#000' : C.muted }}>
                {r.charAt(0).toUpperCase()+r.slice(1)}
              </button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
            {[['Total Sales','$'+totalSales.toFixed(2),C.green],['Transactions',sales.length,C.blue],['Avg Sale','$'+avgSale.toFixed(2),C.text],['Commission','$'+commission.toFixed(2),C.amber]].map(([label,val,color]) => (
              <div key={label} style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: '.1em', marginBottom: 6 }}>{label}</div>
                <div style={{ fontSize: 20, fontWeight: 900, color }}>{val}</div>
              </div>
            ))}
          </div>
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 2 }}>COMMISSION RATE</div>
              <div style={{ fontSize: 13, color: C.text }}>Applied to all sales for payout calculation</div>
            </div>
            {editRate ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="number" value={rateInput} onChange={e => setRateInput(e.target.value)} style={{ ...inp, width: 60 }} autoFocus onKeyDown={e => e.key === 'Enter' && saveRate()} />
                <span style={{ color: C.muted }}>%</span>
                <button onClick={saveRate} style={{ padding: '6px 14px', background: C.amber, color: '#000', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                <button onClick={() => setEditRate(false)} style={{ padding: '6px 12px', background: 'transparent', color: C.muted, border: '1px solid ' + C.border, borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 22, fontWeight: 900, color: C.amber }}>{commissionRate}%</span>
                <button onClick={() => setEditRate(true)} style={{ padding: '5px 12px', background: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.2)', borderRadius: 8, color: C.amber, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Edit</button>
              </div>
            )}
          </div>
          {Object.keys(payBreakdown).length > 0 && (
            <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
              {Object.entries(payBreakdown).map(([method, count]) => (
                <div key={method} style={{ background: (payColors[method]||C.muted)+'15', border: '1px solid '+(payColors[method]||C.muted)+'30', borderRadius: 10, padding: '8px 14px', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: payColors[method]||C.muted, textTransform: 'capitalize' }}>{method}</span>
                  <span style={{ fontSize: 12, color: C.muted }}>{count} sales</span>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '12px 18px', borderBottom: '1px solid ' + C.border, fontWeight: 700, fontSize: 13 }}>Transactions ({sales.length})</div>
            {loading ? <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>Loading...</div>
            : sales.length === 0 ? <div style={{ padding: 40, textAlign: 'center', color: C.muted }}>No sales this {range}</div>
            : sales.map((sale, i) => {
              const items = (() => { try { return JSON.parse(sale.line_items || '[]'); } catch { return []; } })();
              const isExp = expandedSale === (sale.id || i);
              return (
                <div key={sale.id || i} style={{ borderBottom: '1px solid #1a1a22' }}>
                  <div onClick={() => setReceiptSale(sale)}
                    style={{ display: 'flex', alignItems: 'center', padding: '11px 18px', gap: 12, cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background='rgba(245,166,35,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{sale.customer_name || 'Walk-in'}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{formatDateTime(sale.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: (payColors[sale.payment_method]||C.muted)+'20', color: payColors[sale.payment_method]||C.muted, textTransform: 'capitalize' }}>{sale.payment_method}</span>
                    <div style={{ fontSize: 14, fontWeight: 800, color: C.amber }}>${parseFloat(sale.total).toFixed(2)}</div>
                    <div style={{ color: C.muted, fontSize: 11 }}>🧾</div>
                  </div>

                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {receiptSale && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setReceiptSale(null)}>
          <div style={{ background: C.panel, border: '1px solid ' + C.border, borderRadius: 16, padding: 28, width: '100%', maxWidth: 400, maxHeight: '90vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}>
            {/* Receipt Header */}
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <div style={{ fontSize: 36, marginBottom: 6 }}>🧾</div>
              <div style={{ fontSize: 18, fontWeight: 900, color: C.text }}>Receipt</div>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', marginTop: 4 }}>
                #{receiptSale.receipt_number || String(receiptSale.id||'').slice(-6).toUpperCase()}
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{formatDateTime(receiptSale.created_at)}</div>
            </div>
            {/* Staff & Customer */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[['Customer', receiptSale.customer_name || 'Walk-in'], ['Staff', receiptSale.staff_name || '—'], ['Payment', receiptSale.payment_method]].map(([label, val]) => (
                <div key={label} style={{ background: C.bg, borderRadius: 8, padding: '8px 12px' }}>
                  <div style={{ fontSize: 10, color: C.muted, fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, textTransform: 'capitalize' }}>{val}</div>
                </div>
              ))}
            </div>
            {/* Line Items */}
            <div style={{ background: C.bg, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
              {(() => { try { return JSON.parse(receiptSale.line_items || '[]'); } catch { return []; } })().map((item, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '5px 0', borderBottom: '1px solid #1a1a22' }}>
                  <span style={{ color: C.text }}>{item.name} <span style={{ color: C.muted }}>×{item.qty}</span></span>
                  <span style={{ color: C.text, fontWeight: 600 }}>${(parseFloat(item.price||0)*parseInt(item.qty||1)).toFixed(2)}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px dashed ' + C.border, marginTop: 10, paddingTop: 10 }}>
                {receiptSale.discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}>
                    <span>Discount</span><span>-${parseFloat(receiptSale.discount||0).toFixed(2)}</span>
                  </div>
                )}
                {receiptSale.tax > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: C.muted, marginBottom: 4 }}>
                    <span>Tax</span><span>${parseFloat(receiptSale.tax||0).toFixed(2)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: C.text }}>Total</span>
                  <span style={{ fontSize: 20, fontWeight: 900, color: C.amber }}>${parseFloat(receiptSale.total||0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid ' + C.border }}>
                  <span style={{ fontSize: 12, color: C.muted }}>Commission ({commissionRate}%)</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>${(parseFloat(receiptSale.total||0) * commissionRate / 100).toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => window.print()} style={{ flex: 1, padding: 12, background: C.bg, color: C.text, border: '1px solid ' + C.border, borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>🖨️ Print</button>
              <button onClick={() => setReceiptSale(null)} style={{ flex: 2, padding: 12, background: C.amber, color: '#000', border: 'none', borderRadius: 10, fontWeight: 800, cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-surface rounded-lg p-8"><div className="text-2xl text-slate-400">Loading analytics...</div></div>
    </div>
  );
  if (!analytics) return null;
  const metrics = analytics.metrics;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-surface rounded-lg max-w-5xl w-full p-6 my-8 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div><h2 className="text-2xl font-bold text-white">📊 {staffName}</h2></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-3xl leading-none">×</button>
        </div>
        <div className="flex gap-2 mb-6">
          {['today','week','month','all'].map(r => (
            <button key={r} onClick={() => setRange(r)} className={"px-4 py-2 rounded-lg font-semibold capitalize " + (range===r?'bg-primary text-white':'bg-background text-slate-300')}>{r}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[['Total Bookings',metrics.total_bookings,'text-white'],['Completed',metrics.completed,'text-emerald-500'],['Show Rate',metrics.show_rate+'%','text-primary'],['Utilization',metrics.utilization_rate+'%','text-amber-500']].map(([l,v,cls])=>(
            <div key={l} className="bg-background p-4 rounded-lg"><div className="text-slate-400 text-sm mb-1">{l}</div><div className={"text-3xl font-bold "+cls}>{v}</div></div>
          ))}
        </div>
        {metrics.total_bookings === 0 && <div className="text-center py-12"><div className="text-6xl mb-4">📊</div><p className="text-slate-400 text-lg">No bookings for this period</p></div>}
        <button onClick={onClose} className="w-full bg-slate-700 text-white py-3 rounded-lg font-semibold mt-6">Close</button>
      </div>
    </div>
  );
}

export default StaffAnalytics;
